/**
 * cache-browser.ts — Modal UI for browsing and pruning the Uthana cache.
 *
 * Hits `GET /uthana-cache-admin/list` to fetch every cached GLB on the
 * server, renders them as a scrollable list, and lets the developer:
 *
 *   - Filter by prompt or canonical name (live search)
 *   - Preview each clip on the live figure (calls figure.playClipFromUrl)
 *   - Delete entries from the cache (DELETE /uthana-cache-admin/:key)
 *
 * The figure reference is fetched lazily via `figureRef()` because the
 * character can be swapped at runtime — capturing it once would point the
 * preview button at a stale figure.
 *
 * Keyboard shortcuts (when the modal is open):
 *   Esc        — close
 *   ↑ / ↓      — move selection
 *   Enter      — preview the selected clip
 *   Backspace  — delete the selected clip (with native confirm)
 */

import type { Figure } from '../motion/figure';

interface CacheEntry {
  key: string;
  prompt: string;
  name?: string;
  kind: 'beat' | 'hold' | 'locomotion' | 'unknown';
  createdAt: number;
  sizeBytes: number;
  glbUrl: string;
}

interface CacheBrowserOpts {
  serverUrl: string;
  /** Returns the *current* figure — handles character swap. */
  figureRef: () => Figure;
  /** Pre-existing modal root in the DOM — see index.html `#cache-browser`. */
  rootEl: HTMLElement;
}

export class CacheBrowser {
  private opts: CacheBrowserOpts;
  private entries: CacheEntry[] = [];
  private filtered: CacheEntry[] = [];
  private selectedIdx = 0;
  private filter = '';
  private isOpen = false;
  private isLoading = false;
  /**
   * Set when the user clicks the close button (×) or hasn't opened the
   * panel yet. Suppresses auto-show: refreshes that find cached entries
   * will populate the list internally but won't pop the panel into view.
   *
   * Defaults to `true` so the panel stays hidden until the user
   * explicitly opens it (Cache button or C hotkey). The earlier auto-
   * show-on-non-empty behaviour was removed because it competed with
   * the rest of the dev UI; the panel is now strictly user-controlled.
   * Reset to `false` by `open()`/`toggle()` so subsequent auto-show
   * heuristics (which do nothing useful right now but might in future)
   * stay coherent.
   */
  private userHidden = true;
  private static POLL_INTERVAL_MS = 30_000;

  // DOM refs are looked up once on construction and reused per render.
  private listEl: HTMLElement;
  private searchEl: HTMLInputElement;
  private countEl: HTMLElement;
  private totalSizeEl: HTMLElement;
  private statusEl: HTMLElement;

  constructor(opts: CacheBrowserOpts) {
    this.opts = opts;

    // Internal markup is built once at construction so callers only have to
    // provide an empty <div id="cache-browser"> placeholder.
    opts.rootEl.innerHTML = `
      <div class="cache-browser-card">
        <header class="cache-browser-header">
          <div>
            <h2>Uthana cache</h2>
            <span class="cache-browser-summary">
              <span id="cache-count">0</span> clips ·
              <span id="cache-total-size">0 MB</span>
            </span>
          </div>
          <button type="button" class="cache-browser-close" aria-label="Close">×</button>
        </header>
        <div class="cache-browser-toolbar">
          <input
            type="text"
            class="cache-browser-search"
            placeholder="Filter by prompt or name…"
            autocomplete="off"
            spellcheck="false"
          />
          <button type="button" class="cache-browser-refresh" title="Refresh">↻</button>
        </div>
        <div class="cache-browser-status" id="cache-status">Loading…</div>
        <ul class="cache-browser-list" tabindex="0"></ul>
        <footer class="cache-browser-hint">
          Esc close · ↑↓ select · Enter preview · Backspace delete
        </footer>
      </div>
    `;

    this.listEl = opts.rootEl.querySelector('.cache-browser-list')!;
    this.searchEl = opts.rootEl.querySelector('.cache-browser-search')!;
    this.countEl = opts.rootEl.querySelector('#cache-count')!;
    this.totalSizeEl = opts.rootEl.querySelector('#cache-total-size')!;
    this.statusEl = opts.rootEl.querySelector('#cache-status')!;

    // Wire up controls. We keep the listeners on the modal root rather than
    // global so they don't fire while the modal is closed.
    opts.rootEl.querySelector('.cache-browser-close')!
      .addEventListener('click', () => this.closeManual());
    opts.rootEl.querySelector('.cache-browser-refresh')!
      .addEventListener('click', () => { void this.refresh(); });

    this.searchEl.addEventListener('input', () => {
      this.filter = this.searchEl.value;
      this.applyFilterAndRender();
    });

    // Keyboard navigation only fires while the panel is open. Listening at
    // the panel root means it doesn't fight other hotkeys (D, 0–4, etc.).
    opts.rootEl.addEventListener('keydown', (e) => this.onKeyDown(e));

    // Background polling — picks up new clips generated by the director
    // without the user having to hit refresh. Cheap (one HTTP GET every 30s
    // returning a small JSON payload). The timer handle is intentionally
    // discarded; the browser tab lifetime is the timer's lifetime.
    setInterval(() => {
      void this.refresh();
    }, CacheBrowser.POLL_INTERVAL_MS);
  }

  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.userHidden = false; // explicit open re-enables auto-show
    this.opts.rootEl.classList.remove('hidden');
    void this.refresh();
  }

  /**
   * Close from the user's × button — also flips `userHidden` so subsequent
   * background refreshes don't reopen the panel automatically.
   */
  closeManual(): void {
    this.userHidden = true;
    this.isOpen = false;
    this.opts.rootEl.classList.add('hidden');
  }

  /** Programmatic close (no `userHidden` flag) — used internally when the
   *  cache becomes empty so an auto-shown panel goes away. */
  private closeAuto(): void {
    this.isOpen = false;
    this.opts.rootEl.classList.add('hidden');
  }

  toggle(): void {
    if (this.isOpen) this.closeManual();
    else this.open();
  }

  /**
   * Fetch the latest entries from the server and re-render.
   *
   * Called both manually (refresh button, hotkey-toggle open) and from the
   * background poll. Auto-show / auto-hide policy:
   *   - empty cache → close the panel (only if it's currently auto-shown,
   *     so we don't hide a user who explicitly opened it via C)
   *   - non-empty + not user-hidden → show the panel
   *   - user clicked × earlier → leave hidden until they hit C
   */
  async refresh(): Promise<void> {
    if (this.isLoading) return;
    this.isLoading = true;
    this.statusEl.textContent = 'Loading…';
    this.statusEl.classList.remove('hidden');
    try {
      const res = await fetch(`${this.opts.serverUrl}/uthana-cache-admin/list`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as {
        count: number;
        total_size_bytes: number;
        entries: CacheEntry[];
      };
      this.entries = data.entries;
      this.totalSizeEl.textContent = formatSize(data.total_size_bytes);
      this.applyFilterAndRender();
      if (this.entries.length === 0) {
        this.statusEl.textContent = 'Cache is empty.';
        this.statusEl.classList.remove('hidden');
        // Empty cache → auto-hide the panel so it's not just a useless
        // floating box. The user can re-show via C if they want to peek.
        if (this.isOpen && !this.userHidden) this.closeAuto();
      } else {
        this.statusEl.classList.add('hidden');
        // Non-empty + user hasn't dismissed → make sure the panel is visible.
        if (!this.isOpen && !this.userHidden) {
          this.isOpen = true;
          this.opts.rootEl.classList.remove('hidden');
        }
      }
    } catch (err) {
      this.statusEl.textContent = `Error loading cache: ${(err as Error).message}`;
      this.statusEl.classList.remove('hidden');
    } finally {
      this.isLoading = false;
    }
  }

  // ── Private ──────────────────────────────────────────────────────────────

  private applyFilterAndRender(): void {
    const needle = this.filter.trim().toLowerCase();
    this.filtered = needle
      ? this.entries.filter((e) =>
          (e.name ?? '').toLowerCase().includes(needle) ||
          e.prompt.toLowerCase().includes(needle) ||
          e.kind.toLowerCase().includes(needle),
        )
      : this.entries.slice();

    this.countEl.textContent = String(this.filtered.length);
    if (this.selectedIdx >= this.filtered.length) {
      this.selectedIdx = Math.max(0, this.filtered.length - 1);
    }
    this.render();
  }

  private render(): void {
    // Build the row markup as a single innerHTML write — much cheaper than
    // creating individual elements per row when the cache is large.
    const rowsHtml = this.filtered.map((entry, idx) => {
      const sel = idx === this.selectedIdx ? ' is-selected' : '';
      const label = entry.name
        ? escapeHtml(entry.name)
        : `<span class="cache-row-prompt">${escapeHtml(entry.prompt)}</span>`;
      const kindTag = `<span class="cache-row-kind cache-row-kind--${entry.kind}">${entry.kind}</span>`;
      const meta = `${formatAge(entry.createdAt)} · ${formatSize(entry.sizeBytes)}`;
      // Title attribute surfaces age + size + full prompt as a tooltip,
      // since the compact row layout doesn't show them inline.
      const tooltip = escapeHtml(`${meta}\n\n${entry.prompt}`);
      return `
        <li class="cache-row${sel}" data-idx="${idx}" data-key="${entry.key}" title="${tooltip}">
          <div class="cache-row-main">
            ${kindTag}
            <span class="cache-row-label">${label}</span>
          </div>
          <div class="cache-row-meta">${meta}</div>
          <div class="cache-row-actions">
            <button type="button" class="cache-row-btn cache-row-play" title="Preview on figure">▶</button>
            <button type="button" class="cache-row-btn cache-row-delete" title="Delete">×</button>
          </div>
        </li>
      `;
    }).join('');

    this.listEl.innerHTML = rowsHtml;

    // Single delegated click handler instead of per-row listeners.
    this.listEl.onclick = (e) => {
      const target = e.target as HTMLElement;
      const row = target.closest('.cache-row') as HTMLElement | null;
      if (!row) return;
      const idx = Number(row.dataset['idx']);
      const entry = this.filtered[idx];
      if (!entry) return;

      if (target.classList.contains('cache-row-play')) {
        this.preview(entry);
      } else if (target.classList.contains('cache-row-delete')) {
        void this.deleteEntry(entry);
      } else {
        // Click on the row body — just select.
        this.selectedIdx = idx;
        this.render();
      }
    };

    // Keep the selected row visible when arrow-keying past the viewport.
    const selectedEl = this.listEl.querySelector('.cache-row.is-selected');
    if (selectedEl instanceof HTMLElement) {
      selectedEl.scrollIntoView({ block: 'nearest' });
    }
  }

  private preview(entry: CacheEntry): void {
    const figure = this.opts.figureRef();
    // Loop the preview so the user can study the motion. The next director
    // beat (or another preview) overrides it.
    figure
      .playClipFromUrl(`${this.opts.serverUrl}${entry.glbUrl}`, 0.3, true)
      .catch((err) => console.warn('[cache-browser] preview failed:', err));
  }

  private async deleteEntry(entry: CacheEntry): Promise<void> {
    const label = entry.name ?? entry.prompt.slice(0, 60);
    if (!confirm(`Delete "${label}"?\n\nThis removes the cached GLB and its metadata.`)) {
      return;
    }
    try {
      const res = await fetch(
        `${this.opts.serverUrl}/uthana-cache-admin/${entry.key}`,
        { method: 'DELETE' },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Remove locally so the UI updates without a round-trip refresh.
      this.entries = this.entries.filter((e) => e.key !== entry.key);
      this.applyFilterAndRender();
    } catch (err) {
      alert(`Delete failed: ${(err as Error).message}`);
    }
  }

  private onKeyDown(e: KeyboardEvent): void {
    if (!this.isOpen) return;

    // Don't intercept typing in the search box (except Esc/Enter/arrows).
    const inSearch = e.target === this.searchEl;

    if (e.key === 'Escape') {
      e.preventDefault();
      this.closeManual();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.selectedIdx = Math.min(this.selectedIdx + 1, this.filtered.length - 1);
      this.render();
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.selectedIdx = Math.max(this.selectedIdx - 1, 0);
      this.render();
      return;
    }

    if (e.key === 'Enter' && !inSearch) {
      e.preventDefault();
      const entry = this.filtered[this.selectedIdx];
      if (entry) this.preview(entry);
      return;
    }

    if (e.key === 'Backspace' && !inSearch) {
      e.preventDefault();
      const entry = this.filtered[this.selectedIdx];
      if (entry) void this.deleteEntry(entry);
      return;
    }
  }
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatAge(createdAtMs: number): string {
  const ageMs = Date.now() - createdAtMs;
  const sec = ageMs / 1000;
  if (sec < 60) return 'just now';
  const min = sec / 60;
  if (min < 60) return `${Math.floor(min)}m ago`;
  const hr = min / 60;
  if (hr < 24) return `${Math.floor(hr)}h ago`;
  const days = hr / 24;
  return `${Math.floor(days)}d ago`;
}
